import React, { useContext, useState } from 'react';
import { MappingContext, MappingContextType } from '../Contexts/MappingContext';
import SidePanel from 'Components/DesignSystem/SidePanel/SidePanel';
import IconButton from 'Components/DesignSystem/IconButton/IconButton';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { Cross, ChevronLeft, ChevronRight } from 'Components/DesignSystem/Iconography/Icons';
import InfiniteScrollFilterList from './InfiniteScrollFilterList';
import { getPaginatedFilterOptions } from '../Services/MappingServices';

const FilterSelectionMenu = () => {
    const [currentFilterView, setCurrentFilterView] = useState<string | null>(null);

    const {
        activeTab,
        isFilterOpen,
        setIsFilterOpen,
        filterState,
        toggleFilterValue,
        channelKey,
        selectedAttribute
    } = useContext<MappingContextType>(MappingContext);

    const closeFilter = () => {
        setIsFilterOpen(false);
        setCurrentFilterView(null);
    };

    const handleBackToMain = () => {
        setCurrentFilterView(null);
    };

    // Filter Selection Logic
    const handleSharedFilterToggle = (filterType: 'consider' | 'review', value: string) => {
        toggleFilterValue(filterType, value);
    };

    const handleTabFilterToggle = (field: string, value: string) => {
        const filterPath = getTabFilterPath(field);
        toggleFilterValue(filterPath, value);
    };

    const getTabFilterPath = (field: string): string => {
        switch (activeTab) {
            case 'attribute':
                return `attributes.${field}`;
            case 'attributevalue':
                return `attributeValues.${field}`;
            case 'category':
                return `categories.${field}`;
            default:
                return '';
        }
    };

    const getTabFilterValues = (field: string): string[] => {
        switch (activeTab) {
            case 'attribute':
                return filterState.attributes[field as keyof typeof filterState.attributes] || [];
            case 'attributevalue':
                return filterState.attributeValues[field as keyof typeof filterState.attributeValues] || [];
            case 'category':
                return filterState.categories[field as keyof typeof filterState.categories] || [];
            default:
                return [];
        }
    };

    const getFieldLabels = () => {
        switch (activeTab) {
            case 'attribute':
                return {
                    incoming: 'Incoming Attributes',
                    mapped: 'Mapped Attributes'
                };
            case 'attributevalue':
                return {
                    incoming: 'Incoming Attributes Values',
                    mapped: 'Mapped Attributes Values'
                };
            case 'category':
                return {
                    reportingCategory: 'Reporting Categories',
                    mappedCategory: 'Mapped Categories'
                };
            default:
                return { incoming: '', mapped: '' };
        }
    };

    const fieldLabels = getFieldLabels();
    const tabFields = activeTab === 'category' ? ['reportingCategory', 'mappedCategory'] : ['incoming', 'mapped'];

    // The Back + heading row every detail view opens with.
    const detailHeader = (heading: string) => (
        <div className="tw-flex tw-mt-1 tw-items-center tw-gap-3">
            <IconButton
                size="sm"
                aria-label="Back"
                data-testid="filter-back-button"
                onClick={handleBackToMain}
            >
                <ChevronLeft size={20} />
            </IconButton>
            <Typography variant="title-header" as="h3" className="tw-text-ink">
                {heading}
            </Typography>
        </div>
    );

    /*
        The checkbox testids (`consider-${option}-checkbox`, `review-${option}-checkbox`,
        `${title}-${option}-checkbox` in InfiniteScrollFilterList) kept their NAMES but
        moved from the wrapping <label> to the real <input> when the rows became DS
        Checkboxes (2026-09-05) — which also removed the duplicated static inner-input
        testids (`components-filterselectionmenu-input-1/-2`) flagged in
        okf/mapping/e2e.md § 7.1. A spec can click and assert checked-ness on the one
        testid directly now.
    */
    const staticCheckboxView = (
        heading: string,
        options: readonly string[],
        selectedList: string[],
        filterType: 'consider' | 'review',
    ) => (
        <div className="tw-h-full tw-flex tw-flex-col">
            {detailHeader(heading)}

            <div className="tw-flex-1 tw-overflow-y-auto tw-mt-2">
                {options.map((option) => {
                    const value = option.toLowerCase();
                    const isSelected = selectedList.includes(value);

                    return (
                        <div key={option} className="tw-block">
                            <Checkbox
                                label={option}
                                checked={isSelected}
                                onChange={() => handleSharedFilterToggle(filterType, value)}
                                data-testid={`${filterType}-${option}-checkbox`}
                                className="tw-w-full tw-py-2 hover:tw-bg-surface-hover tw-transition-colors"
                            />
                            <div className="tw-w-full tw-h-[1px] tw-bg-edge"></div>
                        </div>
                    );
                })}
            </div>
        </div>
    );

    // Main filter menu
    const renderMainFilterMenu = () => (
        <div className="tw-overflow-y-auto tw-h-full">
            {/* Tab-Specific Filters */}
            {tabFields.map(field => {
                const fieldKey = activeTab === 'category' ? field : field;
                const label = (activeTab === 'category'
                    ? fieldLabels[field as keyof typeof fieldLabels]
                    : fieldLabels[field as keyof typeof fieldLabels]) || '';

                return (
                    <div key={fieldKey} className="tw-block">
                        <div role="button"
                            className="tw-flex tw-items-center tw-justify-between tw-cursor-pointer hover:tw-bg-surface-hover tw-text-ink-secondary"
                            onClick={() => setCurrentFilterView(fieldKey)}
                            data-testid={`filter-${fieldKey}`}
                        >
                            <span className="tw-justify-start tw-py-2 tw-text-ink tw-text-sm tw-font-semibold tw-leading-tight">{label}</span>
                            <ChevronRight size={20} />
                        </div>
                        <div className="tw-w-full tw-h-[1px] tw-bg-edge"></div>
                    </div>
                );
            })}

            {/* Consider Filter */}
            <div className="tw-block">
                <div role="button"
                    className="tw-flex tw-items-center tw-justify-between tw-cursor-pointer hover:tw-bg-surface-hover tw-text-ink-secondary"
                    onClick={() => setCurrentFilterView('consider')}
                    data-testid="filter-consider"
                >
                    <span className="tw-justify-start tw-py-2 tw-text-ink tw-text-sm tw-font-semibold tw-leading-tight">Consider</span>
                    <ChevronRight size={20} />
                </div>
                <div className="tw-w-full tw-h-[1px] tw-bg-edge"></div>
            </div>

            {/* Review Filter */}
            <div className="tw-block">
                <div role="button"
                    className="tw-flex tw-items-center tw-justify-between tw-cursor-pointer hover:tw-bg-surface-hover tw-text-ink-secondary"
                    onClick={() => setCurrentFilterView('review')}
                    data-testid="filter-review"
                >
                    <span className="tw-justify-start tw-py-2 tw-text-ink tw-text-sm tw-font-semibold tw-leading-tight">Review</span>
                    <ChevronRight size={20} />
                </div>
                <div className="tw-w-full tw-h-[1px] tw-bg-edge"></div>
            </div>
        </div>
    );

    // Helper to determine field type (incoming/mapped) from current filter view
    const getFieldType = (filterView: string): 'incoming' | 'mapped' | null => {
        if (filterView === 'incoming' || filterView === 'reportingCategory') return 'incoming';
        if (filterView === 'mapped' || filterView === 'mappedCategory') return 'mapped';
        return null;
    };

    // Individual filter detail view
    const renderFilterDetailView = () => {
        if (!currentFilterView) return null;

        // For Consider and Review filters - use the old static approach
        if (currentFilterView === 'consider') {
            return staticCheckboxView('Consider', ['On', 'Off'], filterState.consider, 'consider');
        }

        if (currentFilterView === 'review') {
            return staticCheckboxView('Review', ['Reviewed', 'Pending'], filterState.review, 'review');
        }

        // For tab-specific filters (incoming/mapped) - use InfiniteScrollFilterList
        const fieldType = getFieldType(currentFilterView);
        if (!fieldType || !channelKey) return null;

        const title = (activeTab === 'category'
            ? fieldLabels[currentFilterView as keyof typeof fieldLabels]
            : fieldLabels[currentFilterView as keyof typeof fieldLabels]) || '';

        const selectedValues = getTabFilterValues(currentFilterView);

        const fetchOptions = async (page: number, search: string) => {
            const attributeName = activeTab === 'attributevalue' && selectedAttribute
                ? selectedAttribute.attribute
                : undefined;

            const result = await getPaginatedFilterOptions(
                channelKey,
                activeTab as MappingTabType,
                fieldType,
                page,
                search,
                attributeName
            );

            if (result && result.done && result.data) {
                let options: string[] = [];
                let hasMore = false;

                // Extract options and hasMore based on field type and tab
                if (activeTab === 'attribute') {
                    options = fieldType === 'incoming' ? result.data.attributes || [] : result.data.mapped_attributes || [];
                    hasMore = fieldType === 'incoming' ? result.data.has_more_incoming : result.data.has_more_mapped;
                } else if (activeTab === 'attributevalue') {
                    options = fieldType === 'incoming' ? result.data.attribute_values || [] : result.data.mapped_attribute_values || [];
                    hasMore = fieldType === 'incoming' ? result.data.has_more_incoming : result.data.has_more_mapped;
                } else if (activeTab === 'category') {
                    options = fieldType === 'incoming' ? result.data.reporting_categories || [] : result.data.mapped_categories || [];
                    hasMore = fieldType === 'incoming' ? result.data.has_more_incoming : result.data.has_more_mapped;
                }

                return { options, hasMore: hasMore || false };
            }

            return { options: [], hasMore: false };
        };

        return (
            <div className="tw-h-full tw-flex tw-flex-col">
                {detailHeader(title)}

                <InfiniteScrollFilterList
                    title={title}
                    selectedValues={selectedValues}
                    onToggle={(value) => handleTabFilterToggle(currentFilterView, value)}
                    fetchOptions={fetchOptions}
                    showSearch={true}
                />
            </div>
        );
    };

    // Only render the panel if filter is open
    if (!isFilterOpen) {
        return null;
    }

    /*
        DS SidePanel (2026-09-05) in place of the hand-rolled fixed overlay + white
        column. Scrim click and Escape both route through onClose -> closeFilter, so
        the detail-view reset behaves exactly as the old overlay click did (Escape is
        new). Backdrop/panel testids follow SidePanel's derivation:
        `filter-modal-backdrop` / `filter-modal-content`.
    */
    return (
        <SidePanel
            isOpen
            onClose={closeFilter}
            width="md"
            title="Filter"
            tabs={[]}
            ariaLabel="Filter"
            data-testid="filter-modal"
            headerActions={
                <IconButton
                    size="sm"
                    aria-label="Close"
                    id="mapping-filter-close-btn"
                    data-testid="components-filterselectionmenu-div-5"
                    onClick={closeFilter}
                >
                    <Cross size={16} />
                </IconButton>
            }
        >
            {currentFilterView ? renderFilterDetailView() : renderMainFilterMenu()}
        </SidePanel>
    );
};

export default FilterSelectionMenu;